import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Spin } from 'antd';
import { createChart, IChartApi, ISeriesApi, CandlestickData, Time, ColorType, CreatePriceLineOptions } from 'lightweight-charts';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import { formatDecimal } from '../../utils/numberFormat';

export interface Candle {
  time: string | number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface ChartPriceLine {
  price: number;
  color: string;
  lineWidth?: number;
  lineStyle?: number; // 0=Solid, 1=Dotted, 2=Dashed, 3=LargeDashed
  title?: string;
}

interface CandlestickChartProps {
  candles?: Candle[];
  height?: number;
  symbol?: string;
  interval?: string;
  priceData?: any | null;
  headerExtra?: React.ReactNode;
  /** When true, shows loading overlay (e.g. when parent is fetching new OHLC for a different scale) */
  loading?: boolean;
  /** Horizontal price lines to draw on the chart */
  priceLines?: ChartPriceLine[];
  /** Number of OHLC bars visible on initial load (default: 80) */
  visibleBars?: number;
  /** Called when user scrolls near the left edge — load older candles */
  onLoadMore?: () => void;
  /** Divisor for header price display (e.g. 1000 for VND stocks). Default: 1 */
  priceDivisor?: number;
}

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height > 0 ? `${$height}px` : '100%'};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  z-index: 10;
  flex-shrink: 0;

  /* One unwrappable row of symbol + three price chips + the interval and view controls is
     about 590px. It only overflowed once a quote arrived, because the chips do not render
     without one. */
  @media (max-width: 991px) {
    flex-wrap: wrap;
    gap: 4px 8px;
  }
`;

/** Pushed to the right edge with margin-left:auto, replacing the container's
 *  space-between — that stopped working once the price chips became a third child. */
const HeaderExtra = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  min-width: 0;
`;

const SymbolTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  margin: 0;
  line-height: 1;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  /* Its own line below: the symbol and the interval/view controls already fill a 390px
     row on their own. The order property puts it last however the JSX is arranged. */
  @media (max-width: 991px) {
    order: 3;
    flex-basis: 100%;
    flex-wrap: wrap;
    gap: 2px 10px;
  }
`;

const CurrentPrice = styled.span<{ $isPositive: boolean }>`
  color: ${({ theme, $isPositive }) => $isPositive ? theme.colors.trading.buy : theme.colors.trading.sell};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  line-height: 1;
`;

const PriceChange = styled.span<{ $isPositive: boolean }>`
  color: ${({ theme, $isPositive }) => $isPositive ? theme.colors.trading.buy : theme.colors.trading.sell};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  line-height: 1;
`;

const ChartContainer = styled.div<{ $height: number }>`
  flex: 1;
  position: relative;
  min-height: ${({ $height }) => $height > 0 ? `${$height - 60}px` : '0'};
  height: ${({ $height }) => $height > 0 ? `${$height - 60}px` : '100%'};
  width: 100%;
  overflow: hidden;
`;

// Helper function to remove prefix (e.g., "HNXDS:", "HSX:", "HNX:") from symbol
const removeSymbolPrefix = (symbol: string): string => {
  if (!symbol) return symbol;
  const colonIndex = symbol.indexOf(':');
  return colonIndex !== -1 ? symbol.substring(colonIndex + 1) : symbol;
};

const CandlestickChart: React.FC<CandlestickChartProps> = ({
  candles: initialCandles,
  height = 500,
  symbol = 'VN30F2511',
  interval = '1min',
  priceData,
  headerExtra,
  loading: externalLoading = false,
  priceLines,
  visibleBars = 80,
  onLoadMore,
  priceDivisor = 1,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const lastValidPriceRef = useRef<number | null>(null); // Track last valid price
  const hasUserScrolledRef = useRef<boolean>(false); // Track if user has manually scrolled
  const initialLoadRef = useRef<boolean>(true); // Track if this is the initial load
  const isProgrammaticUpdateRef = useRef<boolean>(false); // Track if update is programmatic
  const lastProgrammaticRangeRef = useRef<{ from: Time; to: Time } | null>(null); // Track last programmatic range
  // Dedup the priceData effect against re-runs that carry the exact same
  // (price, timestamp) payload (React StrictMode double-mounts, broker
  // retransmits of an identical snapshot). Previously this tracked just the
  // second-level timestamp, which silently dropped every sub-second tick —
  // the chart appeared to freeze for ~900ms then jump, looking laggy. Pairing
  // the timestamp with the price lets legitimate intra-second updates flow
  // through while still rejecting true no-op re-deliveries.
  const lastProcessedTickRef = useRef<{ price: number; ts: number } | null>(null);
  const lastCandlesRef = useRef<Candle[]>([]); // Mirror of candles for same-minute live updates without re-render
  const onLoadMoreRef = useRef(onLoadMore); // Keep callback ref fresh without re-creating chart
  onLoadMoreRef.current = onLoadMore;
  // Mirror of priceLines so the series autoscaleInfoProvider can widen the price
  // range to include the lines even when there are zero candles (e.g. OHLC fetch
  // timed out). Without this, grid config lines sit outside the visible range and
  // appear "not loaded".
  const priceLinesRef = useRef<ChartPriceLine[]>(priceLines ?? []);
  priceLinesRef.current = priceLines ?? [];

  // If the parent supplies the `candles` prop (even an empty array), it owns OHLC fetching.
  // This prevents the chart from "racing" an internal fetch and briefly showing the wrong candle/price.
  const parentProvidesCandles = useRef(false);
  useEffect(() => {
    if (Array.isArray(initialCandles)) {
      parentProvidesCandles.current = true;
    }
  }, [initialCandles]);

  const [candles, setCandles] = useState<Candle[]>(() => {
    if (initialCandles && initialCandles.length > 0) return initialCandles;
    return [];
  });
  const [currentPrice, setCurrentPrice] = useState<number | null>(() => {
    const initial = initialCandles && initialCandles.length > 0 ? initialCandles : [];
    return initial.length > 0 ? initial[initial.length - 1].close : null;
  });

  // Reset chart state when the symbol OR the timeframe changes. `cachedDataLoaded`
  // is what stops the OHLC fetch below from re-running every render, so leaving it
  // set across a timeframe switch kept the old series on screen and made the
  // interval picker look broken. The view refs go with it: bars at a new scale
  // make the previous visible range and scroll position meaningless.
  useEffect(() => {
    setCandles([]);
    setCachedDataLoaded(false);
    // Only "loading" when there is actually something to load. With an empty
    // symbol (no instrument picked yet) nothing clears this flag: the fetch below
    // bails on `!symbol`, and the `initialCandles` effect only runs when the
    // parent passes an array — so the overlay used to spin forever, hiding the
    // empty grid the callers deliberately want to show in that state.
    setChartLoading(Boolean(symbol));
    initialLoadRef.current = true;
    hasUserScrolledRef.current = false;
    lastCandlesRef.current = [];
    lastProcessedTickRef.current = null;
  }, [symbol, interval]);

  // Sync candles when parent provides new data (e.g. OHLC from API)
  useEffect(() => {
    if (initialCandles && initialCandles.length > 0) {
      setCandles(initialCandles);
      setCachedDataLoaded(true);
      setChartLoading(false);
      const last = initialCandles[initialCandles.length - 1];
      setCurrentPrice(last.close);
      const first = initialCandles[0];
      setPriceChange(((last.close - first.close) / first.close) * 100);
      initialLoadRef.current = true;
      hasUserScrolledRef.current = false;
    } else if (Array.isArray(initialCandles)) {
      // Parent owns OHLC fetching but returned no candles (empty result or error)
      setCandles([]);
      setCurrentPrice(null);
      setCachedDataLoaded(true);
      setChartLoading(false);
    }
  }, [initialCandles]);
  const [priceChange, setPriceChange] = useState<number>(0);
  const [latestPriceData, setLatestPriceData] = useState<{
    formattedPrice?: string;
    formattedChangePercent?: string;
    changePercent?: number;
  } | null>(null);
  const [cachedDataLoaded, setCachedDataLoaded] = useState<boolean>(false);
  // Seeded from `symbol` so a chart mounted without one never flashes the overlay
  // on the first paint (effects run after render).
  const [chartLoading, setChartLoading] = useState<boolean>(Boolean(symbol));

  const coreService = apiService.getService(ServiceType.CORE);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const containerWidth = chartContainerRef.current.clientWidth;
    const containerHeight = chartContainerRef.current.clientHeight;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#848E9C',
        fontSize: 11,
      },
      grid: {
        vertLines: {
          color: '#2B3139',
          style: 1,
          visible: true,
        },
        horzLines: {
          color: '#2B3139',
          style: 1,
          visible: true,
        },
      },
      width: containerWidth,
      height: containerHeight,
      autoSize: false,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#2B3139',
        borderVisible: true,
        fixLeftEdge: false,
        fixRightEdge: false,
        rightOffset: 5,
        barSpacing: 8,
        minBarSpacing: 2,
      },
      rightPriceScale: {
        borderColor: '#2B3139',
        borderVisible: true,
        scaleMargins: {
          top: 0.02,
          bottom: 0.3,
        },
      },
      leftPriceScale: {
        visible: true,
        borderColor: '#2B3139',
        borderVisible: false,
        scaleMargins: {
          top: 0.75,
          bottom: 0.02,
        },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: '#758696',
          width: 1,
          style: 3,
          labelBackgroundColor: '#0ECB81',
        },
        horzLine: {
          color: '#758696',
          width: 1,
          style: 3,
          labelBackgroundColor: '#0ECB81',
        },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    // Add candlestick series
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#0ECB81',
      downColor: '#F6465D',
      borderUpColor: '#0ECB81',
      borderDownColor: '#F6465D',
      wickUpColor: '#0ECB81',
      wickDownColor: '#F6465D',
      // Widen the auto-scaled price range to include the grid config price lines.
      // Candlestick price lines don't participate in autoscale by default, so when
      // there are no candles (empty data / fetch timeout) the lines would fall
      // outside the visible range and look missing. Reads the latest lines via ref.
      autoscaleInfoProvider: (orig: () => any) => {
        const base = orig();
        const prices = (priceLinesRef.current ?? [])
          .map((pl) => pl.price)
          .filter((p) => typeof p === 'number' && isFinite(p) && p > 0);
        if (prices.length === 0) return base;
        let minValue = Math.min(...prices);
        let maxValue = Math.max(...prices);
        if (base?.priceRange) {
          minValue = Math.min(minValue, base.priceRange.minValue);
          maxValue = Math.max(maxValue, base.priceRange.maxValue);
        }
        const pad = (maxValue - minValue) * 0.1 || maxValue * 0.05 || 1;
        return { priceRange: { minValue: minValue - pad, maxValue: maxValue + pad } };
      },
    });

    // Volume on left scale — shows volume numbers on the left side, bottom portion only
    const volumeSeries = chart.addHistogramSeries({
      priceScaleId: 'left',
      priceFormat: {
        type: 'volume',
      },
      lastValueVisible: true,
      priceLineVisible: false,
    });

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;
    volumeSeriesRef.current = volumeSeries;

    // Track user scrolling/zooming to prevent auto-scroll
    // Monitor mouse and touch events to detect user interaction
    let scrollCheckTimeout: NodeJS.Timeout | null = null;
    const timeScale = chart.timeScale();
    
    const checkUserScroll = () => {
      if (isProgrammaticUpdateRef.current) {
        isProgrammaticUpdateRef.current = false;
        return;
      }
      
      const currentRange = timeScale.getVisibleRange();
      if (currentRange && lastProgrammaticRangeRef.current && 
          typeof currentRange === 'object' && 'from' in currentRange && 'to' in currentRange) {
        // If the current range differs from what we set, user has scrolled
        if (currentRange.from !== lastProgrammaticRangeRef.current.from || 
            currentRange.to !== lastProgrammaticRangeRef.current.to) {
          hasUserScrolledRef.current = true;
        }
      }
    };
    
    // Use a small delay to check if range changed after our update
    const scheduleScrollCheck = () => {
      if (scrollCheckTimeout) {
        clearTimeout(scrollCheckTimeout);
      }
      scrollCheckTimeout = setTimeout(checkUserScroll, 200);
    };
    
    // Monitor for user interaction (mouse move, touch, etc.)
    const handleUserInteraction = () => {
      scheduleScrollCheck();
    };
    
    // Listen to crosshair moves as an indicator of user interaction
    chart.subscribeCrosshairMove(handleUserInteraction);

    // Load more data when user scrolls near the left edge
    let loadMoreTimeout: NodeJS.Timeout | null = null;
    const handleVisibleRangeChange = (logicalRange: { from: number; to: number } | null) => {
      if (!logicalRange) return;
      // When the left edge of visible range is near bar 0, request more data
      if (logicalRange.from <= 5) {
        if (loadMoreTimeout) clearTimeout(loadMoreTimeout);
        loadMoreTimeout = setTimeout(() => {
          onLoadMoreRef.current?.();
        }, 300);
      }
    };
    timeScale.subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);

    // Handle resize with debouncing to prevent ResizeObserver loop
    let resizeTimeout: NodeJS.Timeout | null = null;
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(() => {
        if (chartContainerRef.current && chartRef.current) {
          const newWidth = chartContainerRef.current.clientWidth;
          const newHeight = chartContainerRef.current.clientHeight;

          if (newWidth > 0 && newHeight > 0) {
            // Use resize() method for better performance
            // Preserve the current visible range to prevent auto-scaling
            const timeScale = chartRef.current.timeScale();
            const visibleRange = timeScale.getVisibleRange();
            
            chartRef.current.resize(newWidth, newHeight);
            
            // Restore visible range after resize to prevent full-screen scaling
            if (visibleRange) {
              timeScale.setVisibleRange(visibleRange);
            }
          }
        }
      }, 100);
    };

    // Use ResizeObserver for better container size tracking
    const resizeObserver = new ResizeObserver((entries) => {
      // Use requestAnimationFrame to defer the resize handling
      requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        handleResize();
      });
    });
    
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (scrollCheckTimeout) {
        clearTimeout(scrollCheckTimeout);
      }
      if (loadMoreTimeout) {
        clearTimeout(loadMoreTimeout);
      }
      timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [height]);

  // Update chart data when candles change
  useEffect(() => {
    if (!candlestickSeriesRef.current || !volumeSeriesRef.current) return;
    if (candles.length === 0) {
      // Clear chart when candles are empty (e.g. symbol change)
      candlestickSeriesRef.current.setData([]);
      volumeSeriesRef.current.setData([]);
      return;
    }

    // Sort candles by time in ascending order before mapping to chart data
    const sortedCandles = [...candles].sort((a, b) => {
      const timeA = typeof a.time === 'string' ? Math.floor(new Date(a.time).getTime() / 1000) : a.time as number;
      const timeB = typeof b.time === 'string' ? Math.floor(new Date(b.time).getTime() / 1000) : b.time as number;
      return timeA - timeB;
    });

    // Remove duplicates - keep only the last candle for each unique timestamp
    const uniqueCandles: Candle[] = [];
    const seenTimestamps = new Set<number>();
    
    // Process in reverse order to keep the latest candle for each timestamp
    for (let i = sortedCandles.length - 1; i >= 0; i--) {
      const candle = sortedCandles[i];
      const timestamp = typeof candle.time === 'string' ? Math.floor(new Date(candle.time).getTime() / 1000) : candle.time as number;
      if (!seenTimestamps.has(timestamp)) {
        seenTimestamps.add(timestamp);
        uniqueCandles.unshift(candle); // Add to beginning to maintain order
      }
    }

    const candleData: CandlestickData[] = uniqueCandles.map(candle => ({
      time: (typeof candle.time === 'string' ? Math.floor(new Date(candle.time).getTime() / 1000) : candle.time) as Time,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    }));

    const volumeData = uniqueCandles.map(candle => ({
      time: (typeof candle.time === 'string' ? Math.floor(new Date(candle.time).getTime() / 1000) : candle.time) as Time,
      value: candle.volume || 0,
      color: candle.close >= candle.open ? 'rgba(14, 203, 129, 0.5)' : 'rgba(246, 70, 93, 0.5)',
    }));

    candlestickSeriesRef.current.setData(candleData);
    volumeSeriesRef.current.setData(volumeData);
    lastCandlesRef.current = uniqueCandles;

    // Only set visible range on initial load, not on every update
    // This allows users to scroll to historical data without being forced back to latest
    if (chartRef.current && sortedCandles.length > 0 && initialLoadRef.current && !hasUserScrolledRef.current) {
      const totalBars = uniqueCandles.length;
      // Show last N bars (visibleBars) with a small right offset for breathing room
      const from = Math.max(0, totalBars - visibleBars);
      const to = totalBars + 5; // small right padding

      // Mark as programmatic update
      isProgrammaticUpdateRef.current = true;

      chartRef.current.timeScale().setVisibleLogicalRange({ from, to });

      // Mark initial load as complete
      initialLoadRef.current = false;
    }
  }, [candles]);

  // Trigger chart rescale when price lines change
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.priceScale('right').applyOptions({ autoScale: true });
    }
  }, [priceLines]);

  // Draw horizontal price lines (upper/lower limits)
  useEffect(() => {
    if (!candlestickSeriesRef.current) return;
    const series = candlestickSeriesRef.current;

    // Remove all existing price lines
    const existingLines = (series as any).__customPriceLines || [];
    existingLines.forEach((line: any) => {
      try { series.removePriceLine(line); } catch (_) {}
    });

    if (!priceLines || priceLines.length === 0) {
      (series as any).__customPriceLines = [];
      return;
    }

    const newLines = priceLines
      .filter(pl => pl.price > 0 && isFinite(pl.price))
      .map(pl => {
        return series.createPriceLine({
          price: pl.price,
          color: pl.color,
          lineWidth: (pl.lineWidth || 1) as CreatePriceLineOptions['lineWidth'],
          lineStyle: pl.lineStyle ?? 2,
          axisLabelVisible: true,
          title: pl.title || '',
        });
      });

    (series as any).__customPriceLines = newLines;

    // With zero candles the price scale has no data range, so the autoscaleInfoProvider
    // (which widens to the price lines) must be re-triggered explicitly — toggling
    // autoScale forces the recompute so the grid config lines become visible.
    if (candles.length === 0) {
      try { series.priceScale().applyOptions({ autoScale: true }); } catch (_) {}
    }
  }, [priceLines, candles]);

  // Calculate initial price change
  useEffect(() => {
    if (candles.length > 0) {
      const firstPrice = candles[0].close;
      const lastPrice = candles[candles.length - 1].close;
      const change = ((lastPrice - firstPrice) / firstPrice) * 100;
      setPriceChange(change);
      setCurrentPrice(lastPrice);
    }
  }, [candles]);

  useEffect(() => {
    if (!symbol || cachedDataLoaded) return;
    // Skip internal fetch when parent is responsible for OHLC
    if (parentProvidesCandles.current) return;
    if (initialCandles && initialCandles.length > 0) return;

    const fetchChartData = async () => {
      try {
        setChartLoading(true);

        const response = await coreService.get('/price/chart', {
          params: { tickerSymbol: removeSymbolPrefix(symbol), timeScale: interval || '1min' }
        });

        if (response.data?.ohlc && Array.isArray(response.data.ohlc)) {
          const fetchedCandles: Candle[] = response.data.ohlc.map((c: any) => ({
            time: typeof c.time === 'string' ? Math.floor(new Date(c.time).getTime() / 1000) : c.time,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
            volume: c.volume || 0
          }));

          const sorted = [...fetchedCandles].sort((a, b) => (a.time as number) - (b.time as number));

          if (sorted.length > 0) {
            setCandles(sorted);

            const last = sorted[sorted.length - 1];
            setCurrentPrice(last.close);
            if (last.close > 0) lastValidPriceRef.current = last.close;

            const first = sorted[0];
            setPriceChange(((last.close - first.close) / first.close) * 100);
          }
        }

        setCachedDataLoaded(true);
        setChartLoading(false);
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setCachedDataLoaded(true);
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [symbol, cachedDataLoaded, initialCandles, interval]);

  // Process price data from parent: same-bucket updates only touch ref + chart (no setState = no re-render). New bar = setCandles once.
  useEffect(() => {
    if (!priceData || !cachedDataLoaded) return;

    const rawLatest = priceData.latest_matched_price;
    let price: number | null =
      rawLatest != null && rawLatest !== undefined ? rawLatest / priceDivisor : null;
    if (price === null || price === undefined) {
      price = lastValidPriceRef.current;
    } else {
      lastValidPriceRef.current = price;
    }
    if (price === null || price === undefined) return;

    const volume = priceData.latest_matched_quantity || 0;

    let timestampUnix: number;
    if (priceData.timestamp_unix) {
      const rawTimestamp = priceData.timestamp_unix;
      timestampUnix = rawTimestamp > 1e12 ? Math.floor(rawTimestamp / 1000) : rawTimestamp;
    } else if (priceData.timestamp) {
      timestampUnix = Math.floor(new Date(priceData.timestamp).getTime() / 1000);
    } else if (priceData.datetime_str) {
      // datetime_str is UTC, e.g. "2026/04/02 06:54:23"
      const parsed = new Date(priceData.datetime_str.replace(/\//g, '-') + 'Z');
      timestampUnix = !isNaN(parsed.getTime()) ? Math.floor(parsed.getTime() / 1000) : Math.floor(Date.now() / 1000);
    } else {
      timestampUnix = Math.floor(Date.now() / 1000);
    }

    // Round timestamp according to the chart's interval
    const roundToInterval = (ts: number): number => {
      switch (interval) {
        case '1day':
        case '1w': {
          // Round to start of day in UTC+7 (subtract 7h, floor to day, add 7h back)
          const utc7 = ts - 7 * 3600;
          const dayStart = Math.floor(utc7 / 86400) * 86400;
          return dayStart + 7 * 3600;
        }
        case '1h':
          return Math.floor(ts / 3600) * 3600;
        case '5min':
          return Math.floor(ts / 300) * 300;
        default: // 1min
          return Math.floor(ts / 60) * 60;
      }
    };

    const bucketTimestamp = roundToInterval(timestampUnix);

    // Skip only true no-op re-deliveries (same price AND same timestamp).
    // Any change in either flows through so intra-second ticks aren't dropped.
    const last = lastProcessedTickRef.current;
    if (last && last.price === price && last.ts === timestampUnix) return;
    lastProcessedTickRef.current = { price, ts: timestampUnix };

    const prevCandles = lastCandlesRef.current;

    // No existing candles — create the first one from live data
    if (prevCandles.length === 0) {
      const firstCandle: Candle = {
        time: bucketTimestamp,
        open: price,
        high: price,
        low: price,
        close: price,
        volume: volume,
      };
      lastCandlesRef.current = [firstCandle];
      setCandles([firstCandle]);
      setCurrentPrice(price);
      return;
    }

    const lastCandle = prevCandles[prevCandles.length - 1];
    const lastCandleTs = typeof lastCandle.time === 'string' ? Math.floor(new Date(lastCandle.time).getTime() / 1000) : (lastCandle.time as number);
    // Round the last candle's time using the SAME interval logic to avoid timezone mismatches
    // (e.g. OHLC data may use UTC midnight while bucketTimestamp uses UTC+7 day boundaries)
    const lastCandleBucket = roundToInterval(lastCandleTs);

    if (bucketTimestamp < lastCandleBucket) return;

    if (bucketTimestamp === lastCandleBucket) {
      // Same bucket: update chart via ref only, no setCandles → no re-render
      const updatedCandle: Candle = {
        ...lastCandle,
        close: price,
        high: Math.max(lastCandle.high, price),
        low: Math.min(lastCandle.low, price),
        volume: (lastCandle.volume || 0) + (volume || 0)
      };

      if (candlestickSeriesRef.current && volumeSeriesRef.current) {
        try {
          // Use the original candle timestamp so it matches the chart's stored data point
          const t = lastCandleTs as Time;
          candlestickSeriesRef.current.update({
            time: t,
            open: updatedCandle.open,
            high: updatedCandle.high,
            low: updatedCandle.low,
            close: updatedCandle.close,
          });
          volumeSeriesRef.current.update({
            time: t,
            value: updatedCandle.volume || 0,
            color: updatedCandle.close >= updatedCandle.open ? 'rgba(14, 203, 129, 0.5)' : 'rgba(246, 70, 93, 0.5)',
          });
        } catch (err) {
          const next = [...prevCandles.slice(0, -1), updatedCandle];
          const candleData = next.map(c => ({
            time: (typeof c.time === 'string' ? Math.floor(new Date(c.time).getTime() / 1000) : c.time) as Time,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
          }));
          const volumeData = next.map(c => ({
            time: (typeof c.time === 'string' ? Math.floor(new Date(c.time).getTime() / 1000) : c.time) as Time,
            value: c.volume || 0,
            color: c.close >= c.open ? 'rgba(14, 203, 129, 0.5)' : 'rgba(246, 70, 93, 0.5)',
          }));
          candlestickSeriesRef.current.setData(candleData);
          volumeSeriesRef.current.setData(volumeData);
        }
      }
      lastCandlesRef.current = [...prevCandles.slice(0, -1), updatedCandle];
      return;
    }

    // New bucket: set state once → [candles] effect runs once and does setData (single render for new bar)
    const newCandle: Candle = {
      time: bucketTimestamp,
      open: lastCandle.close,
      high: price,
      low: price,
      close: price,
      volume: volume
    };
    const newCandles = [...prevCandles, newCandle].sort((a, b) => {
      const timeA = typeof a.time === 'string' ? Math.floor(new Date(a.time).getTime() / 1000) : a.time as number;
      const timeB = typeof b.time === 'string' ? Math.floor(new Date(b.time).getTime() / 1000) : b.time as number;
      return timeA - timeB;
    });
    const seen = new Set<number>();
    const finalCandles: Candle[] = [];
    for (let i = newCandles.length - 1; i >= 0; i--) {
      const c = newCandles[i];
      const ts = typeof c.time === 'string' ? Math.floor(new Date(c.time).getTime() / 1000) : c.time as number;
      if (!seen.has(ts)) {
        seen.add(ts);
        finalCandles.unshift(c);
      }
    }

    lastCandlesRef.current = finalCandles;
    setCandles(finalCandles);
    setCurrentPrice(price);
    setLatestPriceData({
      formattedPrice: priceData.formattedPrice,
      formattedChangePercent: priceData.formattedChangePercent,
      changePercent: priceData.changePercent
    });
    const firstPrice = finalCandles[0].close;
    setPriceChange(((price - firstPrice) / firstPrice) * 100);
  }, [priceData, cachedDataLoaded, priceDivisor, interval]);

  return (
    <ChartWrapper $height={height}>
      <ChartHeader>
          <SymbolTitle>{symbol}</SymbolTitle>
          {(priceData?.ref_price != null || priceData?.ceiling_price != null || priceData?.floor_price != null) && (
            <PriceInfo>
              {priceData?.ref_price != null && (
                <span style={{ color: '#F0B90B', fontSize: '12px', fontWeight: 600 }}>
                  Ref: {formatDecimal(priceData.ref_price / (priceDivisor || 1), 2)}
                </span>
              )}
              {priceData?.ceiling_price != null && (
                <span style={{ color: '#0ECB81', fontSize: '12px', fontWeight: 600 }}>
                  CE: {formatDecimal(priceData.ceiling_price / (priceDivisor || 1), 2)}
                </span>
              )}
              {priceData?.floor_price != null && (
                <span style={{ color: '#F6465D', fontSize: '12px', fontWeight: 600 }}>
                  FL: {formatDecimal(priceData.floor_price / (priceDivisor || 1), 2)}
                </span>
              )}
            </PriceInfo>
          )}
        {headerExtra && <HeaderExtra>{headerExtra}</HeaderExtra>}
      </ChartHeader>

      <ChartContainer ref={chartContainerRef} $height={height}>
        {/* Watermark. Sized against the chart, not in fixed pixels: at 180px flat it was
            half the width of a phone-sized chart and taller than a 30vh one, so it spilled
            past both edges and read as a broken image rather than a watermark. The `min()`
            leaves desktop untouched — 40% only bites once the chart is under ~450px wide —
            and the height cap keeps it inside a short chart. */}
        <img
          src="/Algotrade - No BG.png"
          alt=""
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(180px, 40%)',
            height: 'auto',
            maxHeight: '38%',
            objectFit: 'contain',
            opacity: 0.12,
            pointerEvents: 'none',
            zIndex: 1,
            userSelect: 'none',
          }}
        />
        {/* Separator line between price chart (top 70%) and volume chart (bottom 25%) */}
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '72%',
          height: '1px',
          background: '#2B3139',
          zIndex: 5,
          pointerEvents: 'none',
        }} />
        {(chartLoading || externalLoading) && (
          <>
            <style>{`
              .candlestick-chart-loading .ant-spin-dot-item { background: #0ECB81 !important; }
              .candlestick-chart-loading .ant-spin { color: #0ECB81; }
            `}</style>
            <div
              className="candlestick-chart-loading"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(22, 26, 30, 0.85)',
                borderRadius: 4,
                zIndex: 1000
              }}
            >
              <Spin size="large" />
            </div>
          </>
        )}
      </ChartContainer>
    </ChartWrapper>
  );
};

export default CandlestickChart;
